/**
 * AiAssistantDrawer — Side-drawer UI for the VD Manager AI Assistant.
 * Design ref: user-provided mockup (Feb 2026).
 *
 * Structure: header · greeting · category cards (follow-up / incarichi / attività)
 * with pill badges, Lucide icons, deep-link click handlers, and a chat input.
 *
 * Backend contract is unchanged — reads `insights` from POST /api/crm/assistant.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Sparkles,
    X,
    Calendar,
    Clock,
    ClipboardList,
    User as UserIcon,
    Home as HomeIcon,
    CheckCircle2,
    Send,
    Flame,
    PauseCircle,
    Radar,
    Mic,
    MicOff,
} from "lucide-react";
import LeadDrawer from "./LeadDrawer";
import { Pill, SectionCard, Row, priorityPill, deadlinePill } from "./AiUiKit";

/** Chat message bubble (used when user asks a question). */
function ChatBubble({ role, text }) {
    if (role === "user") {
        return (
            <div className="flex justify-end my-2">
                <div className="max-w-[80%] px-4 py-2.5 rounded-2xl bg-[#007BFF] text-white text-[14px]">
                    {text}
                </div>
            </div>
        );
    }
    return (
        <div className="flex justify-start my-2">
            <div className="max-w-[85%] px-4 py-2.5 rounded-2xl bg-white/5 text-ink-100 text-[14px] whitespace-pre-wrap">
                {text}
            </div>
        </div>
    );
}

/**
 * Main drawer.
 * Props:
 *  - open (bool)
 *  - onClose (fn)
 *  - onNavigateTab (fn(tabId)) — closes drawer + switches VD Manager tab
 */
export default function AiAssistantDrawer({ open, onClose, onNavigateTab }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [plSummary, setPlSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [question, setQuestion] = useState("");
    const [asking, setAsking] = useState(false);
    const [conversation, setConversation] = useState([]);
    const [openLead, setOpenLead] = useState(null);
    const [agents, setAgents] = useState([]);
    const [listening, setListening] = useState(false);
    const [voiceUnsupported, setVoiceUnsupported] = useState(false);
    const sessionRef = useRef(null);
    const chatEndRef = useRef(null);
    const recognitionRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, a, pl] = await Promise.all([
                api.post("/crm/assistant", {}),
                api.get("/crm/agents").catch(() => ({ data: { agents: [] } })),
                api.get("/crm/private-listings/summary").catch(() => ({ data: null })),
            ]);
            setData(r.data);
            sessionRef.current = r.data.session_id;
            setAgents(a.data.agents || []);
            setPlSummary(pl.data);
        } catch (e) {
            console.error("AI drawer load error", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && !data) load();
    }, [open, data, load]);

    // Close on ESC.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Auto-scroll chat to bottom on new message.
    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [conversation, asking]);

    // Voice input via Web Speech API (Chrome/Edge/Safari). Silent fallback if unsupported.
    const toggleVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setVoiceUnsupported(true); return; }
        if (listening) {
            try { recognitionRef.current?.stop(); } catch { /* noop */ }
            setListening(false);
            return;
        }
        try {
            const rec = new SR();
            rec.lang = "it-IT";
            rec.interimResults = true;
            rec.continuous = false;
            rec.onresult = (evt) => {
                let transcript = "";
                for (let i = evt.resultIndex; i < evt.results.length; i++) {
                    transcript += evt.results[i][0].transcript;
                }
                setQuestion(transcript.trim());
            };
            rec.onerror = () => setListening(false);
            rec.onend = () => setListening(false);
            recognitionRef.current = rec;
            rec.start();
            setListening(true);
        } catch (e) {
            console.warn("Speech recognition error", e);
            setListening(false);
        }
    };

    // Stop mic if drawer closes.
    useEffect(() => {
        if (!open && listening) {
            try { recognitionRef.current?.stop(); } catch { /* noop */ }
            setListening(false);
        }
    }, [open, listening]);

    const ask = async () => {
        const q = question.trim();
        if (!q || asking) return;
        setAsking(true);
        setConversation((c) => [...c, { role: "user", text: q }]);
        setQuestion("");
        try {
            const r = await api.post("/crm/assistant", { question: q, session_id: sessionRef.current });
            sessionRef.current = r.data.session_id;
            setConversation((c) => [...c, { role: "ai", text: r.data.ai_text || "Non sono riuscito a generare una risposta." }]);
        } catch {
            setConversation((c) => [...c, { role: "ai", text: "Errore temporaneo, riprova." }]);
        } finally {
            setAsking(false);
        }
    };

    const goToTab = (tabId) => {
        onNavigateTab?.(tabId);
        onClose();
    };

    const firstName = (user?.name || user?.email || "").split(/[ @]/)[0] || "there";
    const ins = data?.insights || {};
    const followUp = (ins.da_ricontattare || []).slice(0, 5);
    const hotLeads = (ins.lead_caldi || []).slice(0, 5);
    const incarichi = (ins.incarichi_in_scadenza || []).slice(0, 5);
    const trattFerme = (ins.trattative_ferme || []).slice(0, 5);
    const senzaAgente = ins.senza_agente || 0;
    const visitePrx = (ins.visite_prossime || []).length;
    const suggestedActions = [
        followUp.length > 0 && `Contatta ${Math.min(3, followUp.length)} lead prioritari`,
        visitePrx > 0 && `Segui ${visitePrx} appuntament${visitePrx === 1 ? "o" : "i"} in programma`,
        incarichi.length > 0 && `Verifica ${incarichi.length} incaric${incarichi.length === 1 ? "o" : "hi"} in scadenza`,
        senzaAgente > 0 && `Assegna ${senzaAgente} lead senza agente`,
    ].filter(Boolean);

    return (
        <>
            {/* Backdrop */}
            <div
                data-testid="ai-drawer-backdrop"
                onClick={onClose}
                aria-hidden="true"
                className={`fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            />

            {/* Drawer panel */}
            <aside
                data-testid="ai-drawer"
                role="dialog"
                aria-label="AI Assistant"
                aria-hidden={!open}
                className={`ai-drawer-scope fixed top-0 right-0 h-screen w-full sm:w-[420px] md:w-[440px] z-[10001] flex-col shadow-2xl transition-transform duration-300 ease-out ${open ? "flex translate-x-0" : "hidden"}`}
                style={{ backgroundColor: "#111111", color: "#FFFFFF" }}
            >
                {/* Header */}
                <div className="px-6 pt-6 pb-4 flex items-start justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-[#0F1E38] border border-[#007BFF]/40 flex items-center justify-center">
                            <Sparkles size={20} className="text-[#5FA7FF]" />
                        </div>
                        <div>
                            <h3 className="text-[17px] font-semibold text-white leading-tight">AI Assistant</h3>
                            <p className="text-[12px] text-ink-400 leading-tight mt-0.5">Il tuo assistente intelligente</p>
                        </div>
                    </div>
                    <button
                        data-testid="ai-drawer-close"
                        onClick={onClose}
                        aria-label="Chiudi assistente"
                        className="p-1 -mr-1 text-ink-400 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable content */}
                <div data-testid="ai-drawer-body" className="flex-1 overflow-y-auto px-6 pb-4 vd-scroll">
                    {loading && (
                        <div className="text-sm text-ink-400 py-10 text-center">Analisi dei tuoi dati CRM in corso...</div>
                    )}

                    {!loading && data && (
                        <>
                            {/* Greeting */}
                            <div className="mb-2">
                                <p className="text-[18px] font-semibold text-white flex items-center gap-1">
                                    Ciao {firstName}! <span aria-hidden="true">👋</span>
                                </p>
                                <p className="text-[14px] text-ink-300 mt-1.5 leading-relaxed">
                                    Ecco il riepilogo delle attività che richiedono la tua attenzione.
                                </p>
                            </div>

                            {/* NUOVI ANNUNCI PRIVATI — visible only if fresh listings exist */}
                            {plSummary && plSummary.new_count > 0 && (
                                <SectionCard
                                    testid="ai-drawer-nuovi-privati"
                                    icon={<Radar size={16} />}
                                    iconClass="text-[#FBBF24]"
                                    title="Nuovi annunci privati"
                                    subtitle="Annunci freschi scoperti dai tuoi monitoraggi"
                                    footerLabel={`Apri Nuovi Privati`}
                                    onFooterClick={() => goToTab("nuovi-privati")}
                                >
                                    <button
                                        data-testid="ai-drawer-pl-summary"
                                        onClick={() => goToTab("nuovi-privati")}
                                        className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-white/5 transition-colors group"
                                    >
                                        <HomeIcon size={16} className="text-[#FBBF24] shrink-0" />
                                        <span className="flex-1 text-[15px] text-white group-hover:text-[#FBBF24] transition-colors">
                                            {plSummary.new_count} annunc{plSummary.new_count === 1 ? "io" : "i"} da esaminare
                                        </span>
                                        <Pill tone="amber">Nuovo</Pill>
                                    </button>
                                    {plSummary.today_count > 0 && plSummary.today_count !== plSummary.new_count && (
                                        <div className="ml-2 text-[12px] text-ink-400 px-2">
                                            +{plSummary.today_count} scoperti oggi · {plSummary.monitors} monitor{plSummary.monitors === 1 ? "" : "aggi"} attivi
                                        </div>
                                    )}
                                </SectionCard>
                            )}

                            {/* FOLLOW-UP GIORNALIERO */}
                            <SectionCard
                                testid="ai-drawer-followup"
                                icon={<Calendar size={16} />}
                                title="Follow-up giornaliero"
                                subtitle="Lead che non rispondono da più tempo"
                                footerLabel={followUp.length > 0 ? `Vedi tutti i ${followUp.length + (ins.da_ricontattare?.length > 5 ? ins.da_ricontattare.length - 5 : 0)} lead` : null}
                                onFooterClick={() => goToTab("crm")}
                            >
                                {followUp.length === 0 && (
                                    <div className="text-[13px] text-ink-500 py-3 px-2">Ottimo: nessun lead trascurato da più di 7 giorni.</div>
                                )}
                                {followUp.map((l) => {
                                    const pri = priorityPill(l.priority);
                                    return (
                                        <Row
                                            key={l.id}
                                            testid={`ai-drawer-lead-${l.id}`}
                                            icon={<UserIcon size={16} />}
                                            label={l.name}
                                            onClick={() => setOpenLead(l.id)}
                                            pills={[
                                                { tone: "red", label: `${l.days_inactive} giorni` },
                                                { tone: pri.tone, label: pri.label },
                                            ]}
                                        />
                                    );
                                })}
                            </SectionCard>

                            {/* LEAD CALDI (shown only if any) */}
                            {hotLeads.length > 0 && (
                                <SectionCard
                                    testid="ai-drawer-hot"
                                    icon={<Flame size={16} />}
                                    iconClass="text-[#FBBF24]"
                                    title="Lead caldi"
                                    subtitle="Alta priorità o in trattativa attiva"
                                    footerLabel={`Vedi tutti nel CRM`}
                                    onFooterClick={() => goToTab("crm")}
                                >
                                    {hotLeads.map((l) => (
                                        <Row
                                            key={l.id}
                                            testid={`ai-drawer-hot-${l.id}`}
                                            icon={<UserIcon size={16} />}
                                            label={l.name}
                                            onClick={() => setOpenLead(l.id)}
                                            pills={[
                                                l.priority === "alta"
                                                    ? { tone: "amber", label: "Alta priorità" }
                                                    : { tone: "blue", label: l.stage || "Attivo" },
                                            ]}
                                        />
                                    ))}
                                </SectionCard>
                            )}

                            {/* INCARICHI IN SCADENZA */}
                            <SectionCard
                                testid="ai-drawer-incarichi"
                                icon={<Clock size={16} />}
                                iconClass="text-[#FBBF24]"
                                title="Incarichi in scadenza"
                                subtitle="Incarichi che scadono nei prossimi giorni"
                                footerLabel={incarichi.length > 0 ? "Vedi tutti gli incarichi" : null}
                                onFooterClick={() => goToTab("contratti")}
                            >
                                {incarichi.length === 0 && (
                                    <div className="text-[13px] text-ink-500 py-3 px-2">Nessun incarico in scadenza nei prossimi 60 giorni.</div>
                                )}
                                {incarichi.map((i) => {
                                    const days = i.days_left;
                                    const label = days < 0 ? `Scaduto da ${-days} giorni` : `Scade tra ${days} giorni`;
                                    const tone = days < 0 || days <= 7 ? "red" : days <= 30 ? "amber" : "gray";
                                    return (
                                        <Row
                                            key={i.lead_id}
                                            testid={`ai-drawer-incarico-${i.lead_id}`}
                                            icon={<HomeIcon size={16} />}
                                            label={i.name}
                                            onClick={() => setOpenLead(i.lead_id)}
                                            pills={[{ tone, label }]}
                                        />
                                    );
                                })}
                            </SectionCard>

                            {/* TRATTATIVE FERME (bonus, shown only if any) */}
                            {trattFerme.length > 0 && (
                                <SectionCard
                                    testid="ai-drawer-ferme"
                                    icon={<PauseCircle size={16} />}
                                    iconClass="text-[#FF6B6B]"
                                    title="Trattative ferme"
                                    subtitle="Ferme da oltre 14 giorni"
                                    footerLabel="Vedi nel CRM"
                                    onFooterClick={() => goToTab("crm")}
                                >
                                    {trattFerme.map((l) => (
                                        <Row
                                            key={l.id}
                                            testid={`ai-drawer-ferma-${l.id}`}
                                            icon={<UserIcon size={16} />}
                                            label={l.name}
                                            onClick={() => setOpenLead(l.id)}
                                            pills={[{ tone: "red", label: `${l.days_inactive} giorni` }]}
                                        />
                                    ))}
                                </SectionCard>
                            )}

                            {/* ATTIVITÀ CONSIGLIATE */}
                            {suggestedActions.length > 0 && (
                                <SectionCard
                                    testid="ai-drawer-attivita"
                                    icon={<ClipboardList size={16} />}
                                    iconClass="text-[#C084FC]"
                                    title="Attività consigliate"
                                    subtitle="Azioni suggerite per oggi"
                                >
                                    {suggestedActions.map((label, i) => (
                                        <div key={i} className="flex items-center gap-3 px-2 py-1.5 text-[15px] text-white">
                                            <CheckCircle2 size={16} className="text-[#5FA7FF] shrink-0" />
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </SectionCard>
                            )}

                            {/* Chat conversation appears above input when user asks something */}
                            {conversation.length > 0 && (
                                <div data-testid="ai-drawer-chat" className="mt-6 pt-4 border-t border-white/8">
                                    {conversation.map((m, i) => (
                                        <ChatBubble key={i} role={m.role} text={m.text} />
                                    ))}
                                    {asking && (
                                        <div className="text-[12px] text-ink-400 animate-pulse ml-2">Sto analizzando…</div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Chat input (pill-shaped) with voice mic */}
                <div className="px-4 pb-5 pt-2 shrink-0" style={{ backgroundColor: "#111111" }}>
                    {voiceUnsupported && (
                        <div className="text-[11px] text-ink-400 mb-2 px-2">
                            Il tuo browser non supporta il riconoscimento vocale. Prova Chrome, Edge o Safari.
                        </div>
                    )}
                    <div className={`flex items-center gap-2 px-2 py-2 rounded-full bg-[#1A1A1A] border transition-colors ${listening ? "border-[#FF4D4D]/60" : "border-white/10 focus-within:border-[#007BFF]/60"}`}>
                        <button
                            data-testid="ai-drawer-mic"
                            onClick={toggleVoice}
                            aria-label={listening ? "Ferma registrazione" : "Parla con l'assistente"}
                            title={listening ? "Sto ascoltando…" : "Chiedi con la voce"}
                            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${listening ? "bg-[#FF4D4D] text-white animate-pulse" : "text-ink-300 hover:bg-white/10"}`}
                        >
                            {listening ? <MicOff size={15} strokeWidth={2.4} /> : <Mic size={15} strokeWidth={2.4} />}
                        </button>
                        <input
                            data-testid="ai-drawer-input"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && ask()}
                            placeholder={listening ? "Ti sto ascoltando…" : "Es. Quanti lead ho in carico?"}
                            className="flex-1 bg-transparent text-[14px] text-white placeholder:text-ink-500 outline-none py-1.5"
                        />
                        <button
                            data-testid="ai-drawer-send"
                            onClick={ask}
                            disabled={asking || !question.trim()}
                            aria-label="Invia domanda"
                            className="w-9 h-9 rounded-full flex items-center justify-center bg-[#007BFF] text-white shrink-0 disabled:opacity-40 hover:bg-[#0069DA] transition-colors"
                        >
                            <Send size={15} strokeWidth={2.5} className="-translate-x-[1px]" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Lead detail modal on top of drawer */}
            {openLead && (
                <LeadDrawer
                    leadId={openLead}
                    agents={agents}
                    onClose={() => setOpenLead(null)}
                    onChanged={() => { load(); }}
                />
            )}
        </>
    );
}
